import { getApiUrl } from "../utils/envConfig";
import { makeAuthenticatedRequest } from "./authApi";
import type { components } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts and returns — CI fails the build if schema.ts is stale.
type ImpersonateStartRequest = components["schemas"]["ImpersonateStartRequest"];
type ImpersonateStartResponse =
  components["schemas"]["ImpersonateStartResponse"];
type ImpersonateStopRequest = components["schemas"]["ImpersonateStopRequest"];
type ImpersonateStopResponse = components["schemas"]["ImpersonateStopResponse"];
type ImpersonateRestoreRequest =
  components["schemas"]["ImpersonateRestoreRequest"];
type ImpersonateRestoreResponse =
  components["schemas"]["ImpersonateRestoreResponse"];

/**
 * Start an impersonation session. Server mints custom Firebase tokens
 * and creates an audit trail. The client cannot bypass the audit.
 * @param {{ target_email: string }} params
 * @returns {Promise<{ session_id, target_token, admin_restore_token, target_email, target_uid, expires_at }>}
 */
export const startImpersonationApi = async ({
  target_email,
}: ImpersonateStartRequest): Promise<ImpersonateStartResponse> => {
  const url = `${getApiUrl()}/admin/impersonate/start`;
  return await makeAuthenticatedRequest(url, {
    method: "POST",
    body: JSON.stringify({ target_email }),
  });
};

/**
 * Stop an impersonation session.
 * @param {{ session_id: string }} params
 * @returns {Promise<{ status: string }>}
 */
export const stopImpersonationApi = async ({
  session_id,
}: ImpersonateStopRequest): Promise<ImpersonateStopResponse> => {
  const url = `${getApiUrl()}/admin/impersonate/stop`;
  return await makeAuthenticatedRequest(url, {
    method: "POST",
    body: JSON.stringify({ session_id }),
  });
};

/**
 * Mint a fresh admin restore token for the active impersonation session.
 * Called on exit while still authenticated as the target; the server authorizes
 * via the impersonation claims on the current token (no admin credential is ever
 * stored client-side, so this survives a page reload mid-impersonation).
 * @param {{ session_id: string }} params
 * @returns {Promise<{ admin_restore_token: string }>}
 */
export const restoreImpersonationApi = async ({
  session_id,
}: ImpersonateRestoreRequest): Promise<ImpersonateRestoreResponse> => {
  const url = `${getApiUrl()}/admin/impersonate/restore`;
  return await makeAuthenticatedRequest(url, {
    method: "POST",
    body: JSON.stringify({ session_id }),
  });
};
